<script setup lang="ts">
  import type { Tab } from '@logistiek-zonder-papier/lzp-library'

  defineProps<{
    tabs: Tab[]
  }>()

  const activeTab = defineModel<string>({ required: true })

  defineSlots<Record<string, () => unknown>>()
</script>

<template>
  <div class="tabs">
    <div class="tab-list" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="tab"
        :class="{ active: activeTab === tab.key }"
        :aria-selected="activeTab === tab.key"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="tab-panel" role="tabpanel">
      <slot :name="activeTab" />
    </div>
  </div>
</template>

<style scoped>
  .tabs {
    display: flex;
    flex-direction: column;
  }

  .tab-list {
    display: flex;
    gap: var(--spacing-s);
    padding: 0 var(--spacing-m);
    border-bottom: 1px solid var(--colours-grey-3);
  }

  .tab {
    margin-bottom: -1px;
    padding: var(--spacing-s) 0.375rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: none;
    color: var(--colours-grey-4);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: color 150ms ease-in-out;
  }

  .tab:hover {
    color: var(--colours-grey-5);
  }

  .tab.active {
    color: var(--colours-grey-5);
    border-bottom-color: var(--colours-grey-5);
  }

  .tab-panel {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    padding: var(--spacing-m);
  }

  @media (prefers-reduced-motion: reduce) {
    .tab {
      transition: none;
    }
  }
</style>
